import { createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode } from 'react';

type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  /** Show a toast with an explicit kind (defaults to "info"). */
  show: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Access the global toast notifier. Must be used under <ToastProvider>. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

const ICON: Record<ToastKind, string> = { success: '✓', error: '✕', info: 'ℹ' };

/**
 * App-wide toast notifications. Mount once near the root; any component can then call
 * useToast() to pop a success/error/info message. Toasts stack and auto-dismiss after 4s,
 * or dismiss immediately when clicked.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const remove = useCallback((id: number) => {
    setItems(list => list.filter(t => t.id !== id));
  }, []);

  const show = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = ++idRef.current;
    setItems(list => [...list, { id, kind, message }]);
    window.setTimeout(() => remove(id), 4000);
  }, [remove]);

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (m: string) => show(m, 'success'),
    error: (m: string) => show(m, 'error'),
    info: (m: string) => show(m, 'info'),
  }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-atomic="false">
        {items.map(t => (
          <div key={t.id} className={`toast toast-${t.kind}`} role="status" onClick={() => remove(t.id)}>
            <span className="toast-icon" aria-hidden="true">{ICON[t.kind]}</span>
            <span className="toast-msg">{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
